import { useCallback, useMemo } from 'react';
import { AccommodationCombined, AccommodationCombinedType, AccommodationSourceType } from '../components/Accommodations/Accommodation.types';
import { withWindow } from '@shared/utils/withWindow';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCustomPageTelemetry } from '../../../Custom.telemetry';
import { useTranslation } from '@shared/core';
import { SearchParams, useHotelBookingRoutePaths, useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { useIncrementInterestedGuestCountMutation } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { EventVenue } from '@shared/utils/eventPlace';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useAccommodationsPrices } from './useAccommodationsPrices';
import { ButtonVariant } from '@withjoy/joykit/components/ButtonV2/Button.types';
import { hasAccommodationLink } from '../components/Accommodations/Accommodation.utils';
import { JoyHotelQueryStringParameters, useHotelDiscount } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { stripDefaultProperties } from '@shared/utils/stripDefaultProperties';

interface AccommodationsCtaArgs {
  accommodation?: AccommodationCombined;
  locationInfo?: EventVenue;
  from?: AccommodationSourceType;
  searchParams: SearchParams;
  onSoldOutItemClickAction?: () => void;
}

export const useAccommodationsCta = (
  props: AccommodationsCtaArgs,
): {
  ctaTitle: string;
  ctaUrl: string;
  ctaVariant: ButtonVariant;
  isLinkEnabled: boolean;
  isShowButton: boolean;
  handleOnSelectAccommodation: (source?: string) => void;
  onSoldOutItemClick: () => void;
} => {
  const { accommodation, locationInfo, from, searchParams, onSoldOutItemClickAction } = props;

  const { eventInfo, eventHandle } = useEventInfo();
  const telemetry = useCustomPageTelemetry();
  const { roomblock, buildPath } = useHotelBookingRoutePaths();
  const [incrementInterestedGuestCount] = useIncrementInterestedGuestCountMutation();
  const [isAccommodationsConsoleEnabled] = useIsFeatureEnabled('travelAccommodationsConsoleV2');
  const { accommodationPlace } = useHotelRoutePaths();
  const joyHotelDiscount = useHotelDiscount();

  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');
  const isEnglish = accTrans.seeAvailability() === 'See Availability';
  const { payload } = useFeatureValue('accommodationsGuestHotelTileExperiment', {
    skip: !isEnglish, // Skip experiment and exposure if its not english
  });
  const isJoyStays = accommodation?.type === AccommodationCombinedType.Hotel && !accommodation?.isCustomLinkEnabled && accommodation?.isFromAccommodationCatalog;

  const hasLink = hasAccommodationLink(accommodation?.isCustomLinkEnabled, accommodation?.customUrl);

  const accommodationsPrices = useAccommodationsPrices(accommodation);
  // Track telemetry only for joy hotels
  const isHotelAvailable = isJoyStays ? !accommodationsPrices?.soldOutUrl : undefined;

  const getCTATitle = useCallback(() => {
    if (accommodationsPrices?.soldOutUrl) {
      return accTrans.exploreHotelsNearby();
    }
    if (isEnglish && payload && isJoyStays) {
      return (payload as { buttonText: string })?.buttonText;
    }

    return accommodation?.type === AccommodationCombinedType.Custom ? accTrans.selectCTACustom() : accTrans.seeAvailability();
  }, [accommodationsPrices?.soldOutUrl, isEnglish, payload, isJoyStays, accommodation?.type, accTrans]);

  const ctaTitle = getCTATitle();

  const ctaUrl = useMemo(() => {
    if (!accommodation?.id || !eventHandle) return '';
    const redirectUrl = withWindow(
      global => {
        let accommodationUrl = accommodationPlace.buildRelativeUrl(
          eventHandle,
          accommodation?.id,
          stripDefaultProperties({
            ...searchParams,
            [JoyHotelQueryStringParameters.joyHotelDiscountRule]: joyHotelDiscount ? joyHotelDiscount.rule : '',
            [JoyHotelQueryStringParameters.joyHotelDiscountCode]: joyHotelDiscount?.code,
          }),
        );
        if (accommodation?.type === AccommodationCombinedType.Roomblock) {
          accommodationUrl = `${global.location.origin}${buildPath(eventHandle, roomblock, accommodation?.id)}`;
        }
        if (accommodationsPrices?.soldOutUrl) {
          accommodationUrl = accommodationsPrices.soldOutUrl;
        }
        return accommodationUrl;
      },
      accommodationPlace.buildRelativeUrl(
        eventHandle,
        accommodation?.id,
        stripDefaultProperties({
          ...searchParams,
          [JoyHotelQueryStringParameters.joyHotelDiscountRule]: joyHotelDiscount?.rule,
          [JoyHotelQueryStringParameters.joyHotelDiscountCode]: joyHotelDiscount?.code,
        }),
      ),
    );
    return redirectUrl;
  }, [accommodation?.id, accommodation?.type, eventHandle, accommodationPlace, searchParams, joyHotelDiscount, accommodationsPrices?.soldOutUrl, buildPath, roomblock]);

  const telemetryHotelInfo = {
    hotelName: accommodation?.displayName || '',
    isHotelPreferred: accommodation?.isFavorite || false,
    hotelPrice: accommodation?.pricePerNight || undefined,
    hotelStrikeThroughPrice: accommodation?.strikeoutPricePerNight || undefined,
    accommodationsType: accommodation?.type,
    from: from || 'not_specified',
  };

  const handleOnSelectAccommodation = useEventCallback((source?: string) => {
    if (accommodation?.type === AccommodationCombinedType.Roomblock) {
      handleOnSelectRoomBlock();
      return;
    }
    handleOnSelectHotel(source);
  });

  const handleOnSelectHotel = useEventCallback((source?: string) => {
    const location = { lat: locationInfo?.latitude || eventInfo?.locationInfo?.latitude, lng: locationInfo?.longitude || eventInfo?.locationInfo?.longitude };
    const venueName = locationInfo?.name ?? eventInfo?.locationInfo?.name ?? '';
    const distance = distanceInMiles({ latitude: accommodation?.latitude, longitude: accommodation?.longitude }, location);
    if (!accommodation?.id || !eventHandle) return;
    const hotel = {
      eventHandle,
      accommodationPlaceId: accommodation?.id,
      hotelName: accommodation?.displayName || '',
      venueName,
      ...(distance && { milesFromVenue: distance.toFixed(1) }),
    };
    telemetry.viewHotelPDPPage(hotel, accommodationsPrices, isHotelAvailable);
    telemetry.accommodationsLinkInteracted(ctaUrl, telemetryHotelInfo, source);
  });

  const onSoldOutItemClick = useCallback(() => {
    if (accommodationsPrices?.soldOutUrl && isAccommodationsConsoleEnabled) {
      onSoldOutItemClickAction?.();
    }
  }, [accommodationsPrices?.soldOutUrl, isAccommodationsConsoleEnabled, onSoldOutItemClickAction]);

  const handleOnSelectRoomBlock = useEventCallback(() => {
    if (!accommodation?.id || !eventHandle) return;

    incrementInterestedGuestCount({
      variables: { reservedRoomBlockId: accommodation?.id },
    });
    const telemetryHotelInfoWithInterestCountIfAvailable = {
      ...telemetryHotelInfo,
      displayedInterestedGuestCount: accommodation?.interestedGuestCount || 0,
    };
    telemetry.accommodationsLinkInteracted(buildPath(eventHandle, roomblock, accommodation?.id), telemetryHotelInfoWithInterestCountIfAvailable);
  });

  const isLinkEnabled = hasLink && ((accommodationsPrices?.soldOutUrl && !isAccommodationsConsoleEnabled) || !accommodationsPrices?.soldOutUrl);

  return {
    ctaTitle,
    ctaUrl,
    ctaVariant: accommodationsPrices?.soldOutUrl ? 'outline' : 'solid',
    isLinkEnabled,
    isShowButton: hasLink,
    handleOnSelectAccommodation,
    onSoldOutItemClick,
  };
};
